// Shared "Attach to a source" panel used inside the Footnote and Endnote
// popovers. Lets the author optionally bind the note to a My Library item
// with a free-form locator (e.g. "p. 42"), or leave it as standalone
// commentary. On attach, the source is also upserted into the Piece's
// references as cited=true so the Citations Preview can resolve canonical
// citation info immediately.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SourceCombobox, type SourceComboboxOption } from "@/components/excerpts/SourceCombobox";
import { useBriefEditorContext } from "../briefEditorContext";
import type { LibraryItem, LibrarySourceType } from "@/lib/library";
import { shortCite } from "@/lib/citations";
import { updateBriefReferenceCited } from "@/lib/composer/core/pieces/references";
import { toast } from "sonner";

type Props = {
  refId: string | null;
  locator: string | null;
  onChange: (next: { refId: string | null; locator: string | null }) => void;
};

const locatorPlaceholder = (type: LibrarySourceType | null): string => {
  switch (type) {
    case "book":
    case "chapter":
      return 'e.g. "p. 42"';
    case "podcast":
      return 'e.g. "12:34"';
    case "journal_article":
      return 'e.g. "p. 211" or "§3"';
    default:
      return 'e.g. "p. 42", "ch. 3", "§3"';
  }
};

export const FootnoteAttachSource = ({ refId, locator, onChange }: Props) => {
  const { workspaceId, briefId, flushPendingSave, notifyChanged } = useBriefEditorContext();
  const [attached, setAttached] = useState<boolean>(!!refId);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setAttached(!!refId);
  }, [refId]);

  useEffect(() => {
    if (!attached || loaded || !workspaceId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("library_items")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("title", { ascending: true })
        .limit(500);
      if (cancelled) return;
      setItems((data ?? []) as LibraryItem[]);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [attached, loaded, workspaceId]);

  const options = useMemo<SourceComboboxOption[]>(
    () =>
      items.map((i) => ({
        id: i.id,
        title: i.title,
        subtitle: null,
        display_title: i.display_title ?? null,
        author_text: i.author_text ?? null,
        source_type: i.source_type,
        container_title: i.container_title ?? null,
      })),
    [items],
  );

  const selectedItem = useMemo(
    () => (refId ? items.find((i) => i.id === refId) ?? null : null),
    [items, refId],
  );

  const preview = useMemo(() => {
    if (!selectedItem) return null;
    const base = shortCite(selectedItem);
    const loc = (locator ?? "").trim();
    return loc ? `${base}, ${loc}` : base;
  }, [selectedItem, locator]);

  const handleToggle = (next: boolean) => {
    setAttached(next);
    if (!next) onChange({ refId: null, locator: null });
  };

  // Upsert the selected library item into the Piece's source references as
  // cited=true so the Citations Preview can render canonical info and the
  // Bibliography tab stays in sync. Best-effort: failures are toasted but the
  // node attachment still persists locally on the footnote/endnote node.
  const ensurePieceReference = async (libraryItemId: string, libraryItem: LibraryItem | undefined) => {
    if (!workspaceId || !briefId) return;
    try {
      const author = libraryItem?.author_text ?? "";
      const year = libraryItem?.published_year ? ` (${libraryItem.published_year})` : "";
      const label = `${author ? `${author}. ` : ""}${libraryItem?.title ?? "Source"}${year}`.trim();
      // Direct upsert into content_piece_references to avoid the
      // `createBriefReferences()` validation path, which internally calls
      // `fetchComposerSnapshot()` and was contributing to the double-load
      // skeleton flash after attaching a source. The unique index
      // `content_piece_references_unique_ref_idx (piece_id, reference_type,
      // reference_id)` makes this safe — on duplicate, we just flip `cited`.
      const { data: existing } = await supabase
        .from("content_piece_references")
        .select("id, cited")
        .eq("workspace_id", workspaceId)
        .eq("piece_id", briefId)
        .eq("reference_type", "source")
        .eq("reference_id", libraryItemId)
        .maybeSingle();
      const row = existing as { id: string; cited: boolean | null } | null;
      if (!row) {
        const { error: insertError } = await supabase
          .from("content_piece_references")
          .insert({
            workspace_id: workspaceId,
            piece_id: briefId,
            reference_type: "source",
            reference_id: libraryItemId,
            label_snapshot: label,
            sort_order: 0,
            cited: true,
          });
        if (insertError) throw insertError;
      } else if (row.cited === false) {
        await updateBriefReferenceCited(workspaceId, row.id, true);
      }
    } catch (err) {
      toast.error((err as Error).message || "Could not attach source to this piece");
    }
  };

  const finalizeAttachmentChange = async () => {
    // Flush the editor's debounced autosave so the node's new refId/locator
    // attributes hit content_piece_drafts immediately, then ask the page to
    // re-pull piece + draft state so Citations Preview reflects the change
    // without a page refresh.
    try {
      await flushPendingSave?.();
    } catch {
      /* surfaced by the save layer's own error handling */
    }
    try {
      await notifyChanged?.();
    } catch {
      /* surfaced by the data-layer */
    }
  };

  const handleSelect = (id: string) => {
    const nextId = id || null;
    onChange({ refId: nextId, locator });
    void (async () => {
      if (nextId) {
        const item = items.find((i) => i.id === nextId);
        await ensurePieceReference(nextId, item);
      }
      await finalizeAttachmentChange();
    })();
  };

  const handleLocator = (next: string) => {
    onChange({ refId, locator: next.trim() ? next : null });
  };

  const handleLocatorCommit = () => {
    void finalizeAttachmentChange();
  };

  const handleDetach = () => {
    setAttached(false);
    onChange({ refId: null, locator: null });
    void finalizeAttachmentChange();
  };

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/20 p-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="footnote-attach" className="text-xs font-medium">
          Attach to a source
        </Label>
        <Switch
          id="footnote-attach"
          checked={attached}
          onCheckedChange={handleToggle}
        />
      </div>

      {attached ? (
        <div className="space-y-2">
          <SourceCombobox
            value={refId ?? ""}
            options={options}
            onChange={(id) => handleSelect(id)}
            placeholder="Search My Library…"
          />
          <div className="space-y-1">
            <Label htmlFor="footnote-locator" className="text-[11px] text-muted-foreground">
              Locator (optional)
            </Label>
            <Input
              id="footnote-locator"
              value={locator ?? ""}
              onChange={(e) => handleLocator(e.target.value)}
              onBlur={handleLocatorCommit}
              placeholder={locatorPlaceholder(selectedItem?.source_type ?? null)}
              className="h-8 text-xs"
            />
          </div>
          {preview ? (
            <p className="text-[11px] leading-5 text-muted-foreground">
              Citation preview: <span className="text-foreground">{preview}</span>
            </p>
          ) : null}
          {refId ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={handleDetach}
            >
              Detach source
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Leave off for author commentary that isn't tied to a source.
        </p>
      )}
    </div>
  );
};
